import { ApprovalsService } from 'api-clients/monolith';

/**
 * One entry in the account's typed field catalog: what the field is, which
 * operators its type allows, and which other fields it may be compared against.
 *
 * `comparable_keys` is computed by the server rather than filtered here. The
 * design mock derives it in the browser (`sameTypeKeys = ALL_DATA_KEYS.filter(
 * k => k !== cond.dataKeyName && dataKeyType(k) === dkType)`), but
 * `ValueType.comparable?` is what `ConditionsValidator` enforces on save — so a
 * client that derived its own rule could offer a pairing the server rejects, and
 * the admin would see a 422 on a combination this builder had just presented as
 * legal.
 */
export type CatalogResponse = Awaited<
  ReturnType<typeof ApprovalsService.getInternalApiApprovalsFieldCatalog>
>;

export type CatalogField = NonNullable<CatalogResponse['fields']>[number];

/**
 * Everything the builder needs the server to have decided for it: the fields,
 * and which operators take no right-hand side.
 *
 * `unaryOperators` is shipped rather than restated here for the same reason
 * `comparable_keys` is. A client-side copy of `ValueType::UNARY_OPERATORS`
 * matches today, but adding one server-side would make this builder demand an
 * operand for it — `validateCondition` returning `no_value` on a condition the
 * server accepts. That is the 422 case in reverse, and it would be silent: the
 * list is load-bearing in validation, in serialisation, and in whether a
 * right-hand side is rendered at all.
 */
export interface FieldCatalog {
  fields: CatalogField[];
  unaryOperators: string[];
}

/**
 * The catalog, or the reason it isn't here yet.
 *
 * Kept as a state rather than flattened to an array on the way in, because
 * "still loading", "the request failed" and "this account has no fields to gate
 * on" are three different things to say to an admin and an empty array says the
 * third one for all of them. On a failed fetch that leaves the builder with an
 * empty field picker, no message and no retry — and on an *existing* rule it is
 * worse, because the stored rows still render while their field select has no
 * matching option, so a rule with live conditions reads as though it had none.
 */
export type CatalogState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; catalog: FieldCatalog };

/** Which side of the comparison the right-hand operand comes from. */
export type CompareTo = 'value' | 'dataKey';

/**
 * How a set of conditions combines (AXHE-3930).
 *
 * One operator per set, not a nested expression: the level's conditions are all
 * ANDed or all ORed. That flatness was the deliberate simplification — it cannot
 * express `(A AND B) OR C`, and `Approvals::Rule::CONDITION_LOGICS` is the same
 * two values, so nothing here is narrower than what can be stored.
 */
export type ConditionLogic = 'and' | 'or';

export interface ConditionDraft {
  /** Stable within an editing session so React keys survive a reorder. */
  key: string;
  dataKeyName: string;
  operator: string;
  compareTo: CompareTo;
  /** Used when compareTo is 'value'. */
  value: string;
  /** Used when compareTo is 'dataKey'. */
  compareKey: string;
}

/**
 * Whether this operator ignores the right-hand side entirely.
 *
 * Answered from the catalog rather than from a constant here — see
 * `FieldCatalog`. A unary condition needs neither a value nor a compare key, so
 * requiring one would block a legal condition.
 */
export const isUnary = (catalog: FieldCatalog, operator: string) =>
  catalog.unaryOperators.includes(operator);

export const emptyCondition = (key: string): ConditionDraft => ({
  key,
  dataKeyName: '',
  operator: '',
  // Comparing to a literal is the common case and the mock's default; the
  // key-to-key mode is opt-in.
  compareTo: 'value',
  value: '',
  compareKey: '',
});

export const fieldFor = (catalog: FieldCatalog, key: string) =>
  catalog.fields.find(field => field.key === key);

/**
 * Change the condition's data key.
 *
 * Resets the operator, the value and the compare key, because all three are
 * only meaningful relative to a type: `contains` is legal on a string and not on
 * a number, "2026-01-01" is a date value and not a boolean, and a compare key of
 * the old type is no longer comparable. Carrying any of them over produces a
 * condition that looks filled in and fails validation on save — the mock resets
 * them for the same reason.
 *
 * `compareTo` is kept when the new field can still be compared to another field,
 * because the admin chose that mode deliberately and re-picking it on every
 * field change would be tedious. It falls back to a literal when the new field
 * has no comparable siblings: the toggle is disabled in that state, so leaving
 * it on `dataKey` strands the condition — the compare-key picker has nothing to
 * offer, `validateCondition` returns `no_compare_key` forever, and the disabled
 * toggle gives the admin no way back to a literal.
 *
 * The catalog is required rather than defaulted: with an unloaded catalog every
 * field looks as though it had no comparable siblings, and this would silently
 * drop a mode the admin deliberately chose. The builder does not render a field
 * picker until the catalog is `ready`, so there is no caller without one.
 */
export const withDataKey = (
  condition: ConditionDraft,
  dataKeyName: string,
  catalog: FieldCatalog,
): ConditionDraft => {
  const comparable = fieldFor(catalog, dataKeyName)?.comparable_keys ?? [];

  return {
    ...condition,
    dataKeyName,
    operator: '',
    value: '',
    compareKey: '',
    compareTo: comparable.length > 0 ? condition.compareTo : 'value',
  };
};

/**
 * Switch between comparing to a literal and comparing to another field.
 *
 * Clears whichever operand is now unused, so a mode the admin moved away from
 * cannot leave a stale value behind in the saved document.
 */
export const withCompareTo = (
  condition: ConditionDraft,
  compareTo: CompareTo,
): ConditionDraft => ({
  ...condition,
  compareTo,
  value: compareTo === 'value' ? condition.value : '',
  compareKey: compareTo === 'dataKey' ? condition.compareKey : '',
});

export type ConditionError =
  | 'no_data_key'
  | 'unknown_data_key'
  | 'no_operator'
  | 'unsupported_operator'
  | 'no_value'
  | 'no_compare_key';

/**
 * Validated against the catalog, not just against emptiness.
 *
 * A stored condition whose field or operator is no longer offered — the key was
 * removed from the account, or the field's `approval_value_type` changed under
 * it — renders as a blank select and would pass a non-empty check, then 422 on
 * save. Naming it at load is the only point at which the admin can act on it.
 */
export const validateCondition = (
  condition: ConditionDraft,
  catalog: FieldCatalog,
): ConditionError | undefined => {
  if (!condition.dataKeyName) return 'no_data_key';

  const field = fieldFor(catalog, condition.dataKeyName);
  if (!field) return 'unknown_data_key';

  if (!condition.operator) return 'no_operator';
  if (!(field.operators ?? []).includes(condition.operator)) {
    return 'unsupported_operator';
  }

  // A unary operator carries no right-hand side at all, so neither operand is
  // required and demanding one would refuse a condition the server accepts.
  if (isUnary(catalog, condition.operator)) return undefined;
  if (condition.compareTo === 'dataKey') {
    return condition.compareKey ? undefined : 'no_compare_key';
  }
  return condition.value.trim() ? undefined : 'no_value';
};

/** The stored shape, as `Approvals::Condition` reads it. */
export interface StoredCondition {
  type: string;
  dataKeyName?: string;
  dataKeyOperator?: string;
  dataKeyCompareTo?: CompareTo;
  dataKeyValue?: string;
  dataKeyCompareKey?: string;
  [key: string]: unknown;
}

export const DATA_KEY_BASED = 'data_key_based';

/**
 * Read stored conditions into drafts.
 *
 * Only `data_key_based` rows are editable here; every other condition type
 * (position, location, opening, compensation, always) is carried through
 * untouched so this builder cannot delete a condition it does not render.
 */
export const draftConditions = (stored: StoredCondition[] = []) =>
  stored
    .filter(row => row.type === DATA_KEY_BASED)
    .map((row, index) => ({
      key: `condition-${index}`,
      dataKeyName: row.dataKeyName ?? '',
      operator: row.dataKeyOperator ?? '',
      compareTo: (row.dataKeyCompareTo ?? 'value') as CompareTo,
      value: row.dataKeyValue ?? '',
      compareKey: row.dataKeyCompareKey ?? '',
    }));

export const passthroughConditions = (stored: StoredCondition[] = []) =>
  stored.filter(row => row.type !== DATA_KEY_BASED);

/**
 * Drafts back to the stored shape, with the untouched rows preserved.
 *
 * The unused operand is omitted rather than sent empty: `Approvals::Condition`
 * reads `dataKeyCompareTo` to decide which side it looks at, and an empty string
 * on the other side is indistinguishable from a value the admin cleared.
 *
 * The literal is stored trimmed, matching what `validateCondition` accepts.
 * Storing `"  Engineering  "` when the check that let it through looked at
 * `"Engineering"` makes an equality comparison against a data key value a silent
 * non-match.
 */
export const toStoredConditions = (
  drafts: ConditionDraft[],
  passthrough: StoredCondition[],
  catalog: FieldCatalog,
): StoredCondition[] => [
  ...passthrough,
  ...drafts.map(draft => {
    const row: StoredCondition = {
      type: DATA_KEY_BASED,
      dataKeyName: draft.dataKeyName,
      dataKeyOperator: draft.operator,
    };

    if (isUnary(catalog, draft.operator)) return row;

    row.dataKeyCompareTo = draft.compareTo;
    if (draft.compareTo === 'dataKey') {
      row.dataKeyCompareKey = draft.compareKey;
    } else {
      row.dataKeyValue = draft.value.trim();
    }
    return row;
  }),
];
